import { useContext, useMemo } from "react";
import {
  CellClassParams,
  ColDef,
  ColGroupDef,
  EditableCallbackParams,
  ICellRendererParams,
  ValueGetterParams
} from "ag-grid-enterprise";
import { useParams } from "react-router";
import {
  coldefDateField,
  coldefHiddenField,
  coldefNavigation,
  coldefSingleField,
  coldefUser,
  dateTimeAuditColumnOverrides
} from "entity-app/shared-components/log-render-components/col-defs";
import {
  ArrayStringCellRenderer,
  ArrayStringGetQuickFilterText,
  featureStatusCellRenderer,
  materialEarlyReleaseCellRenderer,
  twoRowCellRenderer,
  twoRowGetQuickFilterText
} from "entity-app/shared-components/log-render-components/cell-renders";
import {
  bidPackageDueDateHeaderComponent,
  CellCurrencyRendererWithEditIcon,
  CellValueRendererWithEditIcon,
  dueDateCellRenderer,
  featureCommentActionCellRenderer,
  nextDeadlineCellRenderer,
  riskCellRenderer
} from "components/cell-renders";
import numberCellEditor, {
  numberCellEditorParams,
  numberFilterParams
} from "components/cell-editor/numberCellEditor";
import CurrencyCellEditor from "components/cell-editor/currency-cell-editor";
import { EWorkflowStatusDataBlock } from "constants/index";
import { FeatureTypes } from "entity-app/constants";
import DropdownMultiSelectCellEditor from "components/cell-editor/drop-down-multi-select-editor";
import { DateUtils } from "utils/dateutils";
import CustomDateCellEditor from "components/cell-editor/date-cell-editor";
import { buildCustomFieldColumnDefs } from "components/custom-fields/log-page";
import { useEnumValues } from "hooks/enum-values";
import { ProjectContext } from "context/ProjectProvider";
import {
  comparatorWithEmptyAtBottom,
  riskLevelComparator
} from "utils/grid-utils";
import { formatUSCurrency } from "utils/utils";
import { BidScopeCell } from "pages/design-scope-page/shared/bid-scope-cell";
import LinkedDesignPackagesCell from "../components/linked-design-packages-cell";
import { TBidPackageLog } from "../model";
import {
  ONSITE_ESTIMATE_COL_ID,
  ONSITE_ESTIMATE_DEFAULT_LABEL,
  isOnsiteEstimateEditable
} from "../onsite-estimate";
import {
  formatLinkedDesignPackagesForExport,
  getLinkedDesignPackageLabels,
  getLinkedDesignPackagesFilterValue,
  toLinkedDesignPackagesSortKey
} from "./linked-design-packages";

export const usePreconstrutionAgGrid = (props: {
  featureKey: string;
  milestonesColumns: any;
  cannotWriteAccessOfBidPackage: boolean;
  bidPackageHeader: any;
}) => {
  const {
    featureKey,
    milestonesColumns,
    cannotWriteAccessOfBidPackage,
    bidPackageHeader
  } = props;

  const { projectId } = useParams<{ projectId: string }>();

  const {
    columnHeaders: { bidPackageCustomColumns }
  } = useContext(ProjectContext);

  const { enumOptionsMap } = useEnumValues(FeatureTypes.BID_PACKAGE);

  const onsiteEstimateLabel =
    bidPackageHeader?.[ONSITE_ESTIMATE_COL_ID] || ONSITE_ESTIMATE_DEFAULT_LABEL;

  const coldefCustomFields = useMemo(
    () => buildCustomFieldColumnDefs(bidPackageCustomColumns, enumOptionsMap),
    [bidPackageCustomColumns, enumOptionsMap]
  );

  const agGridCheckFieldEditable = (params: CellClassParams<any, any>) => {
    const { data, colDef } = params;
    const colId = colDef.colId || "";
    const fieldEditOnlyWhenNotStarted = [
      "pre_WF_start_float",
      "bid_package_template_id"
    ];
    if (
      fieldEditOnlyWhenNotStarted.includes(colId) &&
      data.workflow_status !== EWorkflowStatusDataBlock.WF_NOT_STARTED
    )
      return false;
    const fieldEditWhenNotComplete = [
      "manual_entry_date",
      ONSITE_ESTIMATE_COL_ID
    ];
    if (
      fieldEditWhenNotComplete.includes(colId) &&
      data.workflow_status === EWorkflowStatusDataBlock.WF_COMPLETED
    )
      return false;
    if (colId === ONSITE_ESTIMATE_COL_ID && !isOnsiteEstimateEditable(data))
      return false;
    return true;
  };

  const isGridCellEditable = (params: CellClassParams<any, any>) => {
    return (
      !cannotWriteAccessOfBidPackage &&
      params?.colDef?.editable &&
      agGridCheckFieldEditable(params)
    );
  };

  const defaultColDef = useMemo(
    () =>
      ({
        sortable: true,
        editable: false,
        filter: true,
        resizable: true,
        minWidth: 100,
        cellRenderer: (params: ICellRendererParams) => {
          return CellValueRendererWithEditIcon(params, true);
        },
        cellEditorPopup: false,
        comparator: comparatorWithEmptyAtBottom
      }) as ColDef<TBidPackageLog>,
    []
  );

  const columnDefs = useMemo(
    () =>
      [
        coldefNavigation(
          {
            field: "auto_inc_value",
            headerName: bidPackageHeader?.id,
            pinned: "left",
            lockPosition: "left",
            initialWidth: 80
          },
          projectId,
          featureKey
        ),
        coldefSingleField({
          field: "number",
          headerName: bidPackageHeader?.number,
          pinned: "left",
          lockPosition: "left",
          initialWidth: 120,
          editable: true,
          cellEditorParams: {
            key: "number"
          }
        }),
        coldefSingleField({
          field: "title",
          headerName: bidPackageHeader?.title,
          initialWidth: 180,
          pinned: "left",
          lockPosition: "left",
          cellRenderer: twoRowCellRenderer,
          cellRendererParams: {
            featureKey,
            projectId
          },
          getQuickFilterText: twoRowGetQuickFilterText,
          lockVisible: true,
          editable: true
        }),
        coldefHiddenField({
          field: "description",
          headerName: bidPackageHeader?.description,
          pinned: "left",
          lockPosition: "left"
        }),
        coldefSingleField({
          field: "wave",
          headerName: bidPackageHeader?.wave,
          initialWidth: 100,
          editable: true
        }),
        coldefSingleField({
          field: "milestone_state",
          headerName: bidPackageHeader?.workflow_status,
          lockVisible: true,
          initialWidth: 200,
          minWidth: 200,
          tooltipField: undefined,
          tooltipValueGetter: undefined,
          editable: true,
          filter: "agSetColumnFilter",
          cellRenderer: featureStatusCellRenderer
        }),
        coldefDateField({
          field: "planned_next_milestone_date",
          headerName: bidPackageHeader?.planned_next_milestone_date,
          initialWidth: 160,
          valueGetter: (params: ValueGetterParams) => {
            return DateUtils.format(params.data?.planned_next_milestone_date);
          },
          cellRenderer: (params: ICellRendererParams) => {
            return nextDeadlineCellRenderer(params, false);
          }
        }),
        coldefDateField({
          field: "projected_next_milestone_date",
          headerName: bidPackageHeader?.projected_next_milestone_date,
          initialWidth: 160,
          valueGetter: (params: ValueGetterParams) => {
            return DateUtils.format(params.data?.projected_next_milestone_date);
          },
          cellRenderer: (params: ICellRendererParams) => {
            return nextDeadlineCellRenderer(params, false);
          }
        }),
        coldefDateField({
          field: "next_planned_deadline",
          headerName: bidPackageHeader?.next_deadline,
          cellRenderer: (params: ICellRendererParams) => {
            return nextDeadlineCellRenderer(params, false);
          }
        }),
        coldefDateField({
          colId: "manual_entry_date",
          field: "manual_entry_date",
          headerName: bidPackageHeader?.manual_entry_date || "Deadline Date",
          editable: true,
          cellRenderer: CellValueRendererWithEditIcon,
          cellEditor: CustomDateCellEditor,
          valueGetter: undefined,
          valueFormatter: ({ value }) => (value ? DateUtils.format(value) : ""),
          cellEditorParams: {
            placeholder: `Select ${
              bidPackageHeader?.manual_entry_date || "Deadline Date"
            }`
          }
        }),
        coldefDateField({
          colId: ONSITE_ESTIMATE_COL_ID,
          field: ONSITE_ESTIMATE_COL_ID,
          headerName: onsiteEstimateLabel,
          // Gate the editor itself (not just the edit icon): completed bid packages and
          // ones planned through linked materials / submittals cannot take a date here.
          editable: (params: EditableCallbackParams<TBidPackageLog>) =>
            !!params.context?.isGridCellEditable?.(params),
          cellRenderer: CellValueRendererWithEditIcon,
          cellEditor: CustomDateCellEditor,
          valueGetter: undefined,
          valueFormatter: ({ value }) => (value ? DateUtils.format(value) : ""),
          cellEditorParams: {
            placeholder: `Select ${onsiteEstimateLabel}`
          },
          tooltipValueGetter: ({ data }) =>
            data && !isOnsiteEstimateEditable(data)
              ? "Required on site is planned through linked Materials or Submittals."
              : undefined
        }),
        coldefDateField({
          field: "final_deadline_date",
          colId: "final_deadline_date",
          headerName: bidPackageHeader?.final_deadline_date || "Deadline",
          headerTooltip: bidPackageHeader?.final_deadline_date || "Deadline",
          valueGetter: (params: ValueGetterParams) => {
            return DateUtils.format(params.data.final_deadline_date);
          },
          cellRenderer: (params: ICellRendererParams) => {
            return nextDeadlineCellRenderer(params, false);
          }
        }),
        coldefDateField({
          field: "projected_last_milestone_date",
          colId: "projected_last_milestone_date",
          headerName:
            bidPackageHeader?.projected_last_milestone_date ||
            "Projected Completion Date",
          headerTooltip:
            bidPackageHeader?.projected_last_milestone_date ||
            "Projected Completion Date",
          valueGetter: (params: ValueGetterParams) => {
            return DateUtils.format(params.data.projected_last_milestone_date);
          },
          cellRenderer: (params: ICellRendererParams) => {
            return nextDeadlineCellRenderer(params, false);
          }
        }),
        coldefDateField({
          field: "due_date",
          headerName: bidPackageHeader?.due_date,
          cellRenderer: (params: ICellRendererParams) => {
            return dueDateCellRenderer(params, false);
          },
          headerComponentParams: bidPackageDueDateHeaderComponent
        }),
        coldefSingleField({
          field: "wf_override_reason",
          colId: "tendered_early",
          headerName:
            bidPackageHeader?.ready_for_release || "Early Bid Package",
          headerTooltip:
            bidPackageHeader?.ready_for_release || "Early Bid Package",
          initialWidth: 160,
          editable: false,
          filter: "agSetColumnFilter",
          cellRenderer: materialEarlyReleaseCellRenderer,
          valueGetter: ({ data }: any) =>
            data?.wf_override_reason ? "Yes" : "No"
        }),
        coldefHiddenField({
          colId: "tendered_early_msg",
          field: "wf_override_reason",
          headerName:
            bidPackageHeader?.early_bid_package_reason ||
            "Reason Cited For Early Bid Package"
        }),
        coldefSingleField({
          field: "risk",
          headerName: bidPackageHeader?.risk_level,
          initialWidth: 130,
          comparator: riskLevelComparator,
          filter: "agSetColumnFilter",
          cellRenderer: (params: ICellRendererParams) => {
            return riskCellRenderer(params, false);
          }
        }),
        coldefSingleField({
          field: "pre_WF_start_float",
          headerName:
            bidPackageHeader?.planned_float || "Bid Package Planned Float",
          initialWidth: 130,
          editable: true,
          cellEditor: "agNumberCellEditor",
          cellEditorParams: numberCellEditorParams,
          filter: "agNumberColumnFilter",
          filterParams: numberFilterParams
        }),
        coldefSingleField({
          field: "effective_float",
          headerName:
            bidPackageHeader?.actual_float || "Bid Package Actual Float",
          initialWidth: 130,
          filter: "agNumberColumnFilter",
          filterParams: numberFilterParams
        }),
        {
          headerName: bidPackageHeader?.design_scope || "Linked Scope Items",
          colId: "designScope",
          minWidth: 170,
          sortable: false,
          // CSV/Excel export reads cell VALUES, not renderers — without this the
          // column exports as a blank header. Export the scope-item count.
          valueGetter: (params) =>
            params.data?.id
              ? (params.context?.getScopeItemsForBid?.(params.data.id)
                  ?.length ?? "")
              : "",
          cellRenderer: (params: {
            data?: { id?: string; number?: string; title?: string };
            context?: {
              getScopeItemsForBid?: (
                id: string
              ) => import("design-scope").DsmScopeItem[];
              onViewScope?: (bidPackageId: string, label: string) => void;
            };
          }) => {
            const id = params.data?.id;
            if (!id) return null;
            const items = params.context?.getScopeItemsForBid?.(id) ?? [];
            const label =
              [params.data?.number, params.data?.title]
                .filter(Boolean)
                .join(" · ") || "Bid Package";
            return (
              <BidScopeCell
                items={items}
                onView={() => params.context?.onViewScope?.(id, label)}
              />
            );
          }
        },
        coldefSingleField({
          colId: "linked_design_packages",
          field: "linked_design_packages",
          headerName:
            bidPackageHeader?.linked_design_packages ||
            "Linked Design Packages",
          // Two 160px tags plus the overflow count, matching Linked Materials.
          initialWidth: 400,
          minWidth: 400,
          editable: false,
          cellDataType: false,
          filter: "agSetColumnFilter",
          filterValueGetter: ({ data }: ValueGetterParams<TBidPackageLog>) =>
            getLinkedDesignPackagesFilterValue(data),
          valueGetter: ({ data }: ValueGetterParams<TBidPackageLog>) =>
            getLinkedDesignPackageLabels(data),
          valueFormatter: ({ value }) =>
            formatLinkedDesignPackagesForExport(value),
          comparator: (valueA, valueB, ...rest) =>
            comparatorWithEmptyAtBottom(
              toLinkedDesignPackagesSortKey(valueA),
              toLinkedDesignPackagesSortKey(valueB),
              ...rest
            ),
          getQuickFilterText: ({ value }) =>
            Array.isArray(value) ? value.join(" ") : "",
          cellRenderer: (params: ICellRendererParams<TBidPackageLog>) => (
            <LinkedDesignPackagesCell
              packages={params.data?.linked_design_packages}
              projectId={projectId}
            />
          )
        }),
        coldefUser({
          def: {
            field: "awarded_subcontractor_name",
            headerName: bidPackageHeader?.awarded_subcontractor,
            editable: true
          },
          user: {
            id: "awarded_subcontractor_id",
            firstName: "awarded_subcontractor_name",
            lastName: "",
            dropdownType: "RC_dropdown"
          }
        }),
        coldefSingleField({
          field: "awarded_value",
          headerName: bidPackageHeader?.awarded_value,
          initialWidth: 160,
          cellRenderer: CellCurrencyRendererWithEditIcon,
          valueFormatter: ({ value }) => formatUSCurrency(value),
          editable: true,
          cellEditor: CurrencyCellEditor,
          filter: "agNumberColumnFilter",
          filterParams: numberFilterParams
        }),
        coldefSingleField({
          field: "estimated_value",
          headerName: bidPackageHeader?.estimated_value,
          initialWidth: 160,
          cellRenderer: CellCurrencyRendererWithEditIcon,
          valueFormatter: ({ value }) => formatUSCurrency(value),
          editable: true,
          cellEditor: CurrencyCellEditor,
          filter: "agNumberColumnFilter",
          filterParams: numberFilterParams
        }),
        coldefUser({
          def: {
            headerName: bidPackageHeader?.preconstruction_lead,
            editable: true
          },
          user: {
            id: "preconstruction_lead_id",
            firstName: "preconstruction_lead_first_name",
            lastName: "preconstruction_lead_last_name",
            statusId: "preconstruction_lead_status_id"
          }
        }),
        coldefUser({
          def: {
            headerName: bidPackageHeader?.operations_lead,
            editable: true
          },
          user: {
            id: "operations_lead_id",
            firstName: "operations_lead_first_name",
            lastName: "operations_lead_last_name",
            statusId: "operations_lead_status_id"
          }
        }),
        coldefUser({
          def: {
            field: "bid_package_template_name",
            headerName: bidPackageHeader?.workflow_template_id,
            editable: true
          },
          user: {
            id: "bid_package_template_id",
            firstName: "bid_package_template_name",
            lastName: "",
            dropdownType: "Template_dropdown",
            featureTypeId: FeatureTypes.BID_PACKAGE
          }
        }),
        coldefSingleField({
          field: "no_of_bidders",
          headerName: bidPackageHeader?.no_of_bidders,
          initialWidth: 160,
          editable: true,
          cellEditor: numberCellEditor,
          filter: "agNumberColumnFilter",
          filterParams: numberFilterParams
        }),
        coldefSingleField({
          field: "bidding_subcontractors",
          headerName: bidPackageHeader?.bidding_subcontractors,
          cellDataType: false,
          getQuickFilterText: ArrayStringGetQuickFilterText,
          cellRenderer: ArrayStringCellRenderer,
          cellClass: "excelWrapText",
          cellRendererParams: {
            // featureKey
          },
          cellEditor: DropdownMultiSelectCellEditor,
          tooltipField: undefined,
          initialWidth: 320,
          minWidth: 310,
          editable: true
        }),
        coldefDateField({
          field: "last_manual_updated_at",
          colId: "last_manual_updated_at",
          headerName:
            bidPackageHeader?.last_manual_updated_at || "Last Updated by User",
          headerTooltip:
            bidPackageHeader?.last_manual_updated_at || "Last Updated by User",
          ...dateTimeAuditColumnOverrides("last_manual_updated_at")
        }),
        coldefDateField({
          field: "last_system_updated_at",
          colId: "last_system_updated_at",
          headerName:
            bidPackageHeader?.last_system_updated_at ||
            "Last Updated by System",
          headerTooltip:
            bidPackageHeader?.last_system_updated_at ||
            "Last Updated by System",
          ...dateTimeAuditColumnOverrides("last_system_updated_at")
        }),
        ...milestonesColumns.allColumns,
        coldefSingleField({
          colId: "action",
          headerName: "Actions",
          headerTooltip: "Actions",
          pinned: "right",
          initialWidth: 80,
          editable: false,
          suppressColumnsToolPanel: true,
          cellRenderer: featureCommentActionCellRenderer,
          filter: false,
          sortable: false,
          menuTabs: [],
          suppressHeaderMenuButton: true,
          suppressHeaderContextMenu: true
        }),
        ...coldefCustomFields
      ] as (ColDef<TBidPackageLog> | ColGroupDef<TBidPackageLog>)[],
    [
      projectId,
      featureKey,
      milestonesColumns.allColumns,
      bidPackageHeader,
      onsiteEstimateLabel,
      coldefCustomFields
    ]
  );
  return {
    columnDefs,
    defaultColDef,
    milestonesColumns,
    isGridCellEditable
  };
};
